import {
  getCurrentUser,
  fetchAuthSession,
  updatePassword,
} from 'aws-amplify/auth';
import { dialogService } from './dialog.service';

/**
 * Retrieves the current Cognito ID token.
 * @returns {Promise<string|undefined>} The serialized ID token, or undefined if no session exists.
 */
export async function getCognitoIdToken() {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

/**
 * Retrieves the current Cognito access token.
 * @returns {Promise<string|undefined>} The serialized access token, or undefined if no session exists.
 */
export async function getCognitoAccessToken() {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.toString();
}

/**
 * Retrieves the expiration time of the current Cognito access token.
 * @returns {Promise<number|undefined>} Unix timestamp (seconds) of token expiry, or undefined if unavailable.
 */
export async function getCognitoAccessTokenExp() {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.payload?.exp;
}

/**
 * Checks if the Cognito access token is expired.
 * Returns true when the token is expired or when the expiry cannot be determined.
 * @returns {Promise<boolean>} True if expired or on error, false if still valid.
 */
export async function isCognitoAccessTokenExpired() {
  try {
    const expirationTime = (await getCognitoAccessTokenExp()) * 1000;
    const currentTime = Date.now();
    return expirationTime < currentTime;
  } catch (error) {
    console.error('Failed to check token expiration: ', error);
    return true;
  }
}

/**
 * Refreshes the current Cognito session and returns a new ID token.
 * In Amplify v6 token refresh is handled automatically via fetchAuthSession({ forceRefresh: true }).
 * @returns {Promise<{token: string|null, error?: Error}>} Object containing the new ID token, or null with the error on failure.
 */
export async function refreshTokenCognito() {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return {
      token: session.tokens?.idToken?.toString() ?? null,
    };
  } catch (error) {
    console.error('Failed to refresh the token ', error);
    return { token: null, error };
  }
}

/**
 * Changes the password for the current authenticated Cognito user.
 * @param {string} oldPassword - The user's current password.
 * @param {string} newPassword - The desired new password.
 * @returns {Promise<'SUCCESS'>} Resolves with 'SUCCESS' on completion.
 * @throws {Error} Re-throws the Cognito error after showing a dialog (error.code is normalised).
 */
export async function changePasswordCognito(oldPassword, newPassword) {
  try {
    await updatePassword({ oldPassword, newPassword });
    return 'SUCCESS';
  } catch (error) {
    error.code = error.code || error.name;
    if (error.code === 'NotAuthorizedException') {
      error.code = 'NotAuthorizedException.ChangePassword';
    }
    handleCognitoError(error);
    throw error;
  }
}

/**
 * Validates that the current Cognito session exists and the access token has not expired.
 * @returns {Promise<{valid: boolean, error?: Error}>} Object with valid=true when the session is active,
 *   or valid=false with the originating error when the session is missing or expired.
 */
export async function isCognitoSessionValid() {
  try {
    await getCurrentUser();
    const session = await fetchAuthSession();
    const exp = session.tokens?.accessToken?.payload?.exp;
    const now = Date.now() / 1000;

    if (exp && exp < now) {
      const error = new Error('Access token expired');
      error.code = 'TokenExpired';
      return { valid: false, error };
    }

    return { valid: true };
  } catch (err) {
    console.warn('[WARN] Cognito session validation failed:', err);
    return { valid: false, error: err };
  }
}

/**
 * Handles errors returned by AWS Cognito and shows a user-friendly error dialog.
 * @param {Error} error - The Cognito error object (expects error.code to be set).
 * @returns {void}
 */
export function handleCognitoError(error) {
  dialogService.cognitoError({ error });
}
